import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
// import { DragDropContext } from 'react-beautiful-dnd';
import { getNid, WebUI } from "../../wui-core/src/index";
import List from './list';
import Operation from './operation';
import Search from './search';
// import { move, reorder } from './utils';
import { getLangInfo } from "../../wui-locale/src/tool";
import i18n from './i18n';
import { TransferProps, TransferKeyType, TransferItem } from './iTransfer';
import { WithConfigConsumer } from '../../wui-provider/src/context';

const defaultProps: TransferProps = {
    dataSource: [],
    targetKeys: [],
    render: (item) => item.title || '',
    showSearch: false,
    locale: 'zh-cn',
    showCheckbox: true,
    draggable: false,
    appendToBottom: false,
    renderOperation: () => '',
    operations: ['rightAll', 'leftOne', 'rightOne', 'leftAll'],
};

function TransferFC(props: TransferProps) {
    const {
        clsPrefix, operations, showSearch, notFoundContent, fieldid,
        searchPlaceholder, body, footer, listStyle, className = '', locale,
        filterOption, render, lazy, showCheckbox, draggable, renderOperation, children,
        style, disabled, dir: direction,
        dataSource = [], targetKeys = [], selectedKeys,
        onChange, onScroll, onSelectChange, appendToBottom,
        titles, rowKey, selectAllLabels = [],
        onSearchChange,
    } = props;

    // 受控/非受控选中keys
    const [leftFilter, setLeftFilter] = useState('');
    const [rightFilter, setRightFilter] = useState('');
    const [sourceSelectedKeys, setSourceSelectedKeys] = useState<TransferKeyType[]>(
        (selectedKeys || []).filter(key => targetKeys.indexOf(key) === -1)
    );
    const [targetSelectedKeys, setTargetSelectedKeys] = useState<TransferKeyType[]>(
        (selectedKeys || []).filter(key => targetKeys.indexOf(key) > -1)
    );

    // 拖拽相关状态及ref
    const [draggingItem, setDraggingItem] = useState<TransferItem | null>(null);
    const [dragFromList, setdragFromList] = useState<'left' | 'right' | null>(null);
    const [dragOverList, setDragOverList] = useState<'left' | 'right' | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [startClient, setStartClient] = useState<{ x: number, y: number } | null>(null);
    const [isDraggingOutside, setIsDraggingOutside] = useState<boolean>(false);

    const leftListRef = useRef<HTMLDivElement>(null);
    const rightListRef = useRef<HTMLDivElement>(null);
    const dragOverIndexRef = useRef<number | null>(null);
    const dragOverListRef = useRef<'left' | 'right' | null>(null);
    const scrollTimerRef = useRef<any | null>(null);
    const rafRef = useRef<number | null>(null);
    const sourceKeysSetRef = useRef<Set<TransferKeyType>>(new Set());
    const targetKeysSetRef = useRef<Set<TransferKeyType>>(new Set());
    const sourceSelectedKeysRef = useRef<TransferKeyType[]>(sourceSelectedKeys);
    const targetSelectedKeysRef = useRef<TransferKeyType[]>(targetSelectedKeys);

    // 受控 selectedKeys
    useEffect(() => {
        if (selectedKeys) {
            setSourceSelectedKeys(selectedKeys.filter(key => targetKeys.indexOf(key) === -1));
            setTargetSelectedKeys(selectedKeys.filter(key => targetKeys.indexOf(key) > -1));
        }
    }, [selectedKeys, targetKeys]);

    // 更新选中keys的Set缓存和ref
    useEffect(() => {
        sourceKeysSetRef.current = new Set(sourceSelectedKeys);
        targetKeysSetRef.current = new Set(targetSelectedKeys);
        sourceSelectedKeysRef.current = sourceSelectedKeys;
        targetSelectedKeysRef.current = targetSelectedKeys;
    }, [sourceSelectedKeys, targetSelectedKeys]);

    // 清理定时器和RAF
    useEffect(() => {
        return () => {
            if (scrollTimerRef.current) {
                clearInterval(scrollTimerRef.current);
                scrollTimerRef.current = null;
            }
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [])

    // 唯一key处理
    const dataSourceWithKey = useMemo(() => {
        if (!rowKey) return dataSource;
        return dataSource.map(item => ({ ...item, key: rowKey(item) }));
    }, [dataSource, rowKey]);

    // 简化数据拆分逻辑，避免过度优化
    const { allSourceKeys, leftDataSource, rightDataSource } = useMemo(() => {
        const allKeys = dataSourceWithKey.map(item => item.key);
        const left: TransferItem[] = [];
        const right: TransferItem[] = [];

        // 简单的数据分组，避免复杂的Set和Map操作
        dataSourceWithKey.forEach(item => {
            if (targetKeys.indexOf(item.key) >= 0) {
                right.push(item);
            } else {
                left.push(item);
            }
        });

        // 只在必要时对右侧数据排序
        const sortedRight = targetKeys.length > 0 && right.length > 1
            ? targetKeys.map(key => right.find(item => item.key === key)).filter(Boolean) as TransferItem[]
            : right;

        return {
            allSourceKeys: allKeys,
            leftDataSource: left,
            rightDataSource: sortedRight
        };
    }, [dataSourceWithKey, targetKeys]);

    // 选中keys变化时自动清空无效key
    useEffect(() => {
        if (!selectedKeys) {
            setSourceSelectedKeys(keys => keys.filter(key => allSourceKeys.includes(key) && targetKeys.indexOf(key) === -1));
            setTargetSelectedKeys(keys => keys.filter(key => allSourceKeys.includes(key) && targetKeys.indexOf(key) > -1));
        }
    }, [allSourceKeys, targetKeys, selectedKeys]);

    // 获取可移动keys - 使用缓存优化性能
    const getMoveKeys = useCallback((direction: string) => {
        if (direction === 'right') return sourceSelectedKeys;
        if (direction === 'left') return targetSelectedKeys;
        if (direction === 'allLeft') {
            return rightDataSource.filter(data => !data.disabled).map(data => data.key);
        }
        return leftDataSource.filter(data => !data.disabled).map(data => data.key);
    }, [sourceSelectedKeys, targetSelectedKeys, leftDataSource, rightDataSource]);

    // 简化按钮状态计算，避免复杂的数组操作
    const buttonStates = useMemo(() => ({
        leftActive: targetSelectedKeys.length > 0 && !disabled,
        rightActive: sourceSelectedKeys.length > 0 && !disabled,
        allLeftActive: !disabled && rightDataSource.length > 0,
        allRightActive: !disabled && leftDataSource.length > 0
    }), [targetSelectedKeys.length, sourceSelectedKeys.length, disabled, rightDataSource.length, leftDataSource.length]);

    // 选中变化
    const handleSelectChange = useCallback((direction: string, holder: TransferKeyType[]) => {
        if (!onSelectChange) return;
        if (direction === 'left') onSelectChange(holder, targetSelectedKeys);
        else onSelectChange(sourceSelectedKeys, holder);
    }, [onSelectChange, sourceSelectedKeys, targetSelectedKeys]);

    // 移动操作
    const moveTo = useCallback((direction: string, insertIndex?: number) => {
        let tKeys = [...targetKeys];
        const moveKeys = getMoveKeys(direction);
        let temp: TransferKeyType[] = [];
        if (appendToBottom) {
            temp = tKeys.concat(moveKeys);
        } else if (insertIndex !== undefined) {
            tKeys.splice(insertIndex, 0, ...moveKeys);
            temp = tKeys;
        } else {
            temp = moveKeys.concat(tKeys);
        }
        const newTargetKeys = direction.toLowerCase().indexOf('right') !== -1
            ? temp
            : tKeys.filter(targetKey => moveKeys.indexOf(targetKey) === -1);
        const oppositeDirection = direction.toLowerCase().indexOf('right') !== -1 ? 'left' : 'right';
        if (!selectedKeys) {
            if (oppositeDirection === 'left') setSourceSelectedKeys([]);
            else setTargetSelectedKeys([]);
        }
        handleSelectChange(oppositeDirection, []);
        onChange && onChange(newTargetKeys, direction, moveKeys);
    }, [targetKeys, appendToBottom, getMoveKeys, onChange, selectedKeys, handleSelectChange]);

    const moveToLeft = useCallback(() => moveTo('left'), [moveTo]);
    const moveToRight = useCallback((insertIndex?: number) => moveTo('right', insertIndex), [moveTo]);
    const allMoveToLeft = useCallback(() => moveTo('allLeft'), [moveTo]);
    const allMoveToRight = useCallback(() => moveTo('allRight'), [moveTo]);

    // 使用 ref 保持函数引用稳定，避免频繁重建
    const handleSelect = useCallback((direction: string, selectedItem: Partial<TransferItem>, checked?: boolean) => {
        const holder = direction === 'left'
            ? [...sourceSelectedKeysRef.current]
            : [...targetSelectedKeysRef.current];
        const index = holder.indexOf(selectedItem.key as string);
        if (checked) holder.splice(index, 1);
        else if (index === -1) holder.push(selectedItem.key as string);
        handleSelectChange(direction, holder);
        if (!selectedKeys) {
            if (direction === 'left') setSourceSelectedKeys(holder);
            else setTargetSelectedKeys(holder);
        }
    }, [handleSelectChange, selectedKeys]);

    // 缓存左右选择函数，避免每次都重新创建
    const stableHandlers = useMemo(() => ({
        handleLeftSelect: (selectedItem: Partial<TransferItem>, checked?: boolean) =>
            handleSelect('left', selectedItem, checked),
        handleRightSelect: (selectedItem: Partial<TransferItem>, checked?: boolean) =>
            handleSelect('right', selectedItem, checked),
    }), [handleSelect]);

    // 拖拽处理
    const handleDragStart = useCallback((e: MouseEvent, item: TransferItem, fromList: 'left' | 'right', index: number) => {
        setStartClient(prev => ({
            ...prev,
            x: e.clientX,
            y: e.clientY
        }))
        setDraggingItem(item)
        setdragFromList(fromList)
        setIsDragging(true)
        setDragOverIndex(index)
        setDragOverList(fromList)
        setIsDraggingOutside(false)
        dragOverListRef.current = fromList
    }, [])
    const handleDragMove = useCallback((e: MouseEvent, item: TransferItem) => {
        if (!isDragging || !startClient) return

        const { x, y } = startClient
        const diffX = e.clientX - x
        const diffY = e.clientY - y
        if (Math.abs(diffX) < 5 && Math.abs(diffY) < 5) return

        // 使用 RAF 节流，避免频繁更新
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
            const SCROLL_ZONE = 50 // 滚动触发区域
            const SCROLL_SPEED = 5 // 滚动速度

            if (scrollTimerRef.current) {
                clearInterval(scrollTimerRef.current)
                scrollTimerRef.current = null
            }

            // 使用缓存的Set，避免每次创建
            if (dragFromList === 'left') {
                if (!sourceKeysSetRef.current.has(item.key)) {
                    stableHandlers.handleLeftSelect(item, false)
                }
            } else if (dragFromList === 'right') {
                if (!targetKeysSetRef.current.has(item.key)) {
                    stableHandlers.handleRightSelect(item, false)
                }
            }

            // 检测拖拽到哪个列表
            const checkOverList = (ref: React.RefObject<HTMLDivElement>) => {
                if (!ref.current) return false

                const rect = ref.current.getBoundingClientRect()
                return e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom
            }

            // 缓存计算结果，减少DOM查询
            const calculateDragIndex = (container: Element, rect: DOMRect, itemHeight: number = 32) => {
                const relativeY = e.clientY - rect.top + container.scrollTop
                return dragFromList === 'left'
                    ? Math.floor(relativeY / itemHeight)
                    : Math.floor(relativeY / itemHeight) - 1
            }

            if (checkOverList(leftListRef)) {
                if (dragOverListRef.current !== 'left') {
                    setDragOverList('left')
                    dragOverListRef.current = 'left'
                }

                // 计算插入位置
                const container = leftListRef.current?.children[1].children[0]
                if (!container) return
                const rect = container.getBoundingClientRect()
                const maxScroll = container.scrollHeight - container.clientHeight

                if (container.scrollTop > 0 && e.clientY < rect.top + SCROLL_ZONE && e.clientY >= rect.top) {
                    scrollTimerRef.current = setInterval(() => {
                        container.scrollTop -= SCROLL_SPEED
                        const index = Math.min(calculateDragIndex(container, rect), leftDataSource.length)
                        setDragOverIndex(index)
                        dragOverIndexRef.current = index
                    }, 11)
                } else if (container.scrollTop < maxScroll && e.clientY > rect.bottom - SCROLL_ZONE && e.clientY <= rect.bottom) {
                    scrollTimerRef.current = setInterval(() => {
                        container.scrollTop += SCROLL_SPEED
                        const index = Math.min(calculateDragIndex(container, rect), leftDataSource.length)
                        setDragOverIndex(index)
                        dragOverIndexRef.current = index
                    }, 11)
                } else {
                    const index = Math.min(calculateDragIndex(container, rect), leftDataSource.length)
                    setDragOverIndex(index)
                    dragOverIndexRef.current = index
                }
            } else if (checkOverList(rightListRef)) {
                if (dragOverListRef.current !== 'right') {
                    setDragOverList('right')
                    dragOverListRef.current = 'right'
                }

                // 计算插入位置
                const container = rightListRef.current?.children[1].children[0]
                if (!container) return
                const rect = container!.getBoundingClientRect()
                const maxScroll = container.scrollHeight - container.clientHeight

                if (container.scrollTop > 0 && e.clientY < rect.top + SCROLL_ZONE && e.clientY >= rect.top) {
                    scrollTimerRef.current = setInterval(() => {
                        container.scrollTop -= SCROLL_SPEED
                        const index = Math.min(calculateDragIndex(container, rect), rightDataSource.length)
                        setDragOverIndex(index)
                        dragOverIndexRef.current = index
                    }, 11)
                } else if (container.scrollTop < maxScroll && e.clientY > rect.bottom - SCROLL_ZONE && e.clientY <= rect.bottom) {
                    scrollTimerRef.current = setInterval(() => {
                        container.scrollTop += SCROLL_SPEED
                        const index = Math.min(calculateDragIndex(container, rect), rightDataSource.length)
                        setDragOverIndex(index)
                        dragOverIndexRef.current = index
                    }, 11)
                } else {
                    const index = Math.min(calculateDragIndex(container, rect), rightDataSource.length)
                    setDragOverIndex(index)
                    dragOverIndexRef.current = index
                }
            } else {
                setIsDraggingOutside(true)
                setDragOverList(null)
                setDragOverIndex(null)
            }
        }); // 关闭 RAF 回调
    }, [isDragging, leftDataSource.length, rightDataSource.length, dragFromList, stableHandlers, startClient])
    const handleDragEnd = useCallback((item: TransferItem) => {
        if (!draggingItem || !dragFromList) {
            resetDragState()
            return
        }

        if (draggable) {
            // 处理拖拽结果
            if (dragOverListRef.current && dragOverListRef.current !== dragFromList) {
                // 跨列表移动
                const moveKeys = dragFromList === 'left' ? sourceSelectedKeys : targetSelectedKeys
                let newTargetKeys = [...targetKeys]

                if (dragFromList === 'left' && dragOverListRef.current === 'right') {
                    // 从左到右
                    if (dragOverIndexRef.current !== null) {
                        moveKeys.sort((a, b) => (parseInt(String(a)) - parseInt(String(b))))
                        newTargetKeys.splice(dragOverIndexRef.current + 1, 0, ...moveKeys)
                    } else {
                        newTargetKeys = newTargetKeys.concat(moveKeys)
                    }
                    onChange && onChange(newTargetKeys, 'right', moveKeys)
                } else if (dragFromList === 'right' && dragOverListRef.current === 'left') {
                    // 从右到左
                    newTargetKeys = newTargetKeys.filter(key => !moveKeys.includes(key))
                    setTargetSelectedKeys([])
                    onChange && onChange(newTargetKeys, 'left', moveKeys)
                }
            } else if (dragOverList === dragFromList && dragOverIndex !== null) {
                // 列表内排序
                if (dragFromList === 'right') {
                    const currentIndex = rightDataSource.findIndex(item => item.key === draggingItem.key)
                    const newTargetKeys = [...targetKeys]
                    const [movedKey] = newTargetKeys.splice(currentIndex, 1)
                    newTargetKeys.splice(dragOverIndex, 0, movedKey)
                    onChange && onChange(newTargetKeys, '', [draggingItem.key])
                    stableHandlers.handleRightSelect(item, false)
                } else if (dragFromList === 'left') {
                    stableHandlers.handleLeftSelect(item, false)
                }
            }
        }

        resetDragState()
    }, [draggingItem, dragFromList, dragOverList, dragOverIndex, sourceSelectedKeys, targetSelectedKeys, targetKeys, leftDataSource, rightDataSource, onChange, stableHandlers])
    const resetDragState = useCallback(() => {
        setDraggingItem(null)
        setdragFromList(null)
        setDragOverList(null)
        setDragOverIndex(null)
        setIsDragging(false)
        setIsDraggingOutside(false)
        setStartClient(null)
        dragOverIndexRef.current = null
        dragOverListRef.current = null
        if (scrollTimerRef.current) {
            clearInterval(scrollTimerRef.current)
            scrollTimerRef.current = null
        }
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current)
            rafRef.current = null
        }
    }, [])

    // 全选
    const handleSelectAll = useCallback((direction: string, filteredDataSource: TransferItem[], checkAll: boolean) => {
	    const holder = checkAll ? [] : filteredDataSource.map(item => item.key);
        handleSelectChange(direction, holder);
        if (!selectedKeys) {
            if (direction === 'left') setSourceSelectedKeys(holder);
            else setTargetSelectedKeys(holder);
        }
    }, [handleSelectChange, selectedKeys]);
    const handleLeftSelectAll = useCallback((filteredDataSource: TransferItem[], checkAll: boolean) => handleSelectAll('left', filteredDataSource, checkAll), [handleSelectAll]);
    const handleRightSelectAll = useCallback((filteredDataSource: TransferItem[], checkAll: boolean) => handleSelectAll('right', filteredDataSource, checkAll), [handleSelectAll]);

    // 搜索/清空
    const handleFilter = useCallback((direction: 'left' | 'right', value: string) => {
        if (direction === 'left') setLeftFilter(value);
        else setRightFilter(value);
        onSearchChange?.(direction, value);
    }, [onSearchChange]);
    const handleClear = useCallback((direction: 'left' | 'right') => handleFilter(direction, ''), [handleFilter]);
    const handleLeftFilter = useCallback((v: string) => handleFilter('left', v), [handleFilter]);
    const handleRightFilter = useCallback((v: string) => handleFilter('right', v), [handleFilter]);
    const handleLeftClear = useCallback(() => handleClear('left'), [handleClear]);
    const handleRightClear = useCallback(() => handleClear('right'), [handleClear]);

    // 滚动
    const handleScroll = useCallback((direction: string, e: React.UIEvent<HTMLElement>) => {
        onScroll && onScroll(direction, e);
    }, [onScroll]);
    const handleLeftScroll = useCallback((e: React.UIEvent<HTMLElement>) => handleScroll('left', e), [handleScroll]);
    const handleRightScroll = useCallback((e: React.UIEvent<HTMLElement>) => handleScroll('right', e), [handleScroll]);

    // 标题
    const local = useMemo(() => getLangInfo(locale, i18n, 'transfer'), [locale]);
    const cls = classNames(className, clsPrefix, {
        [`${clsPrefix}-rtl`]: direction === 'rtl',
    });
    const _titles = titles || [local.langMap.source, local.langMap.target];
    let adapterNid = getNid(props);
    const fieldidProps = fieldid ? { fieldid } : {};

	    return (
	        <div className={cls} {...adapterNid} {...fieldidProps} style={style}>
            <List
                ref={leftListRef}
                titleText={_titles[0]}
                dataSource={leftDataSource}
                filter={leftFilter}
                filterOption={filterOption}
                style={listStyle}
                checkedKeys={sourceSelectedKeys}
                handleFilter={handleLeftFilter}
                handleClear={handleLeftClear}
                handleSelect={stableHandlers.handleLeftSelect}
                handleSelectAll={handleLeftSelectAll}
                handleScroll={handleLeftScroll}
                render={render}
                showSearch={showSearch}
                searchPlaceholder={searchPlaceholder || local.langMap.searchPlaceholder}
                notFoundContent={notFoundContent || local.langMap.notFoundContent}
                body={body}
                footer={footer}
                prefixCls={`${clsPrefix}-list`}
                lazy={lazy}
                showCheckbox={showCheckbox}
                draggable={draggable}
                onDragStart={(e, item, index) => handleDragStart(e, item, 'left', index)}
                onDragMove={(e, item) => handleDragMove(e, item)}
                onDragEnd={(item) => handleDragEnd(item)}
                isDragging={isDragging}
                draggingItem={draggingItem}
                dragOverIndex={dragOverList === 'left' ? dragOverIndex : null}
                dragFromList={dragFromList}
                dragOverList={dragOverList === 'left' ? dragOverList : null}
                isDraggingOutside={isDraggingOutside}
                onResetState={resetDragState}
                fieldid={fieldid ? `${fieldid}_transfer-input-search_left` : undefined}
                direction="left"
                renderList={children}
                disabled={disabled}
                selectAllLabel={selectAllLabels[0]}
            />
            {!draggable ? (
                <Operation
                    dir={direction}
                    rightActive={buttonStates.rightActive}
                    operations={operations}
                    moveToRight={moveToRight}
                    leftActive={buttonStates.leftActive}
                    moveToLeft={moveToLeft}
                    allLeftActive={buttonStates.allLeftActive}
                    allMoveToLeft={allMoveToLeft}
                    allRightActive={buttonStates.allRightActive}
                    allMoveToRight={allMoveToRight}
                    className={`${clsPrefix}-operation`}
                    renderOperation={renderOperation}
                    fieldid={fieldid}
                />
            ) : null}
            <List
                ref={rightListRef}
                titleText={_titles[1]}
                dataSource={rightDataSource}
                filter={rightFilter}
                filterOption={filterOption}
                style={listStyle}
                checkedKeys={targetSelectedKeys}
                handleFilter={handleRightFilter}
                handleClear={handleRightClear}
                handleSelect={stableHandlers.handleRightSelect}
                handleSelectAll={handleRightSelectAll}
                handleScroll={handleRightScroll}
                render={render}
                showSearch={showSearch}
                searchPlaceholder={searchPlaceholder || local.langMap.searchPlaceholder}
                notFoundContent={notFoundContent || local.langMap.notFoundContent}
                body={body}
                footer={footer}
                prefixCls={`${clsPrefix}-list`}
                lazy={lazy}
                showCheckbox={showCheckbox}
                draggable={draggable}
                onDragStart={(e, item, index) => handleDragStart(e, item, 'right', index)}
                onDragMove={(e, item) => handleDragMove(e, item)}
                onDragEnd={(item) => handleDragEnd(item)}
                isDragging={isDragging}
                draggingItem={draggingItem}
                dragOverIndex={dragOverList === 'right' ? dragOverIndex : null}
                dragFromList={dragFromList}
                dragOverList={dragOverList === 'right' ? dragOverList : null}
                isDraggingOutside={isDraggingOutside}
                onResetState={resetDragState}
                fieldid={fieldid ? `${fieldid}_transfer-input-search_right` : undefined}
                direction="right"
                renderList={children}
                disabled={disabled}
                selectAllLabel={selectAllLabels[1]}
            />
	        </div>
	    );
}

const Transfer = WithConfigConsumer({ name: "transfer" })(WebUI({ name: "transfer", defaultProps })(TransferFC));
Transfer.List = List;
Transfer.Operation = Operation;
Transfer.Search = Search;
export default Transfer;
